import { Controller } from "@hotwired/stimulus"

// Client-side state management for Common::BeatSequenceEditorComponent.
//
// The component renders each beat row with hidden inputs whose `name`
// attributes use the `beats[][...]` empty-bracket syntax. Rack parses
// that into a true Array of hashes on submit, with row boundaries
// detected by duplicate keys (every row carries `id` + `kind`, so
// boundaries are unambiguous). Form-submit order = DOM order = parsed
// array order, so reorder / add / remove operations only need to
// manipulate DOM nodes — there's no per-row index to maintain.
//
// Newly-added rows (via `addBeat`) clone from a `<template>` block
// that ships with the page; the template's hidden inputs already carry
// the correct `beats[][...]` names, so a cloned row is immediately
// submittable.
export default class extends Controller {
  static targets = ["list", "addKind", "speakingTemplate", "silentTemplate", "gapTemplate"]
  static values = {
    templateKey: String,
    characterId: Number,
    characterSlug: String,
    language: String,
  }

  connect() {
    this.refreshDisabledStates()
  }

  addBeat() {
    if (!this.hasAddKindTarget) return
    const kind = this.addKindTarget.value
    const template = this.templateForKind(kind)
    if (!template) return
    const id = this.generateBeatId()
    const fragment = template.content.cloneNode(true)
    fragment.querySelectorAll("[value='__ID__']").forEach((el) => { el.value = id })
    fragment.querySelectorAll("[data-beat-id='__ID__']").forEach((el) => { el.setAttribute("data-beat-id", id) })
    this.listTarget.appendChild(fragment)
    this.refreshDisabledStates()
    this.notifyPreview()
  }

  remove(event) {
    const row = this.rowFor(event.currentTarget)
    if (!row) return
    if (row.dataset.beatKind === "core") return
    row.remove()
    this.refreshDisabledStates()
    this.notifyPreview()
  }

  moveUp(event) {
    const row = this.rowFor(event.currentTarget)
    if (!row) return
    const prev = row.previousElementSibling
    if (prev) {
      this.listTarget.insertBefore(row, prev)
      this.refreshDisabledStates()
      this.notifyPreview()
    }
  }

  moveDown(event) {
    const row = this.rowFor(event.currentTarget)
    if (!row) return
    const next = row.nextElementSibling
    if (next) {
      this.listTarget.insertBefore(next, row)
      this.refreshDisabledStates()
      this.notifyPreview()
    }
  }

  // Structural edits (add / remove / reorder) change the beats array the
  // form will submit, but they mutate the DOM without any native form
  // event — so the live-preview controller (remotion-preview: a
  // document-level bubbling input/change listener filtered by field-name
  // prefix) never re-renders. Re-announce via one of the remaining rows'
  // hidden inputs: its `name` carries the
  // `video_config[config_data][<template>][beats][][…]` path the preview's
  // prefix filter matches. The core row can never be removed, so a named
  // input is always available.
  notifyPreview() {
    if (!this.hasListTarget) return
    const input = this.listTarget.querySelector("input[name]")
    if (!input) return
    input.dispatchEvent(new Event("input", { bubbles: true }))
    input.dispatchEvent(new Event("change", { bubbles: true }))
  }

  // Updates move-up / move-down button disabled states to reflect the
  // current DOM order. No hidden input name rewriting is needed —
  // names use `beats[][...]` and rely on DOM order.
  refreshDisabledStates() {
    if (!this.hasListTarget) return
    const rows = Array.from(this.listTarget.querySelectorAll(".bse__row"))
    rows.forEach((row, index) => {
      const upBtn = row.querySelector("[data-action*='moveUp']")
      const downBtn = row.querySelector("[data-action*='moveDown']")
      if (upBtn) upBtn.toggleAttribute("disabled", index === 0)
      if (downBtn) downBtn.toggleAttribute("disabled", index === rows.length - 1)
    })
  }

  templateForKind(kind) {
    if (kind === "speaking" && this.hasSpeakingTemplateTarget) return this.speakingTemplateTarget
    if (kind === "silent"   && this.hasSilentTemplateTarget)   return this.silentTemplateTarget
    if (kind === "gap"      && this.hasGapTemplateTarget)      return this.gapTemplateTarget
    return null
  }

  rowFor(buttonEl) {
    return buttonEl.closest(".bse__row")
  }

  generateBeatId() {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID().slice(0, 8)
    }
    return Math.random().toString(36).slice(2, 10)
  }
}
